import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { 
  ArrowLeft, 
  Globe, 
  RefreshCw, 
  Copy,
  MessageCircle,
  Settings,
  BarChart3,
  MessageSquare,
  Brain,
  Mail,
  ChevronRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import SystemPromptModal from '../components/SystemPromptModal';
import SitemapConfigModal from '../components/SitemapConfigModal';

// New Contact Messages Component
const NewContactMessages = ({ clientId }) => {
  const { data: newMessages, isLoading } = useQuery(
    ['newContactMessages', clientId],
    async () => {
      const response = await axios.get(`/contact/submissions/${clientId}?page=1&limit=5&status=new`);
      return response.data;
    },
    {
      refetchInterval: 30000, // Refresh every 30 seconds
    }
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('lt-LT', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  const submissions = newMessages?.submissions || [];
  const newCount = newMessages?.statusCounts?.new || 0;

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-gray-900">
          Nauji kontaktai
          {newCount > 0 && (
            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              {newCount}
            </span>
          )}
        </h2>
        <Link
          to={`/clients/${clientId}/contact-submissions`}
          className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500"
        >
          Žiūrėti visus
          <ChevronRight className="ml-1 h-4 w-4" />
        </Link>
      </div>
      
      {submissions.length === 0 ? (
        <div className="text-center py-8">
          <Mail className="mx-auto h-8 w-8 text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">Nėra naujų kontaktų</p>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.slice(0, 3).map((submission) => (
            <div key={submission._id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center mb-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {submission.name}
                    </p>
                    <span className="ml-2 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      Nauja
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mb-2">{submission.email}</p>
                  <p className="text-sm text-gray-700 line-clamp-2">
                    {submission.message}
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <p className="text-xs text-gray-500">
                    {formatDate(submission.submittedAt)}
                  </p>
                </div>
              </div>
            </div>
          ))}
          
          {newCount > 3 && (
            <div className="pt-3 border-t border-gray-200">
              <Link
                to={`/clients/${clientId}/contact-submissions`}
                className="block text-center text-sm text-indigo-600 hover:text-indigo-500"
              >
                Ir dar {newCount - 3} nauji kontaktai...
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ClientDetail = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [showApiKey, setShowApiKey] = useState(false);
  const [showSystemPromptModal, setShowSystemPromptModal] = useState(false);
  const [showSitemapModal, setShowSitemapModal] = useState(false);

  const { data: client, isLoading } = useQuery(
    ['client', id],
    async () => {
      const response = await axios.get(`/clients/${id}`);
      return response.data;
    }
  );

  const regenerateKeyMutation = useMutation(
    async () => {
      const response = await axios.post(`/clients/${id}/regenerate-key`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client', id]);
        toast.success('API key regenerated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to regenerate API key');
      }
    }
  );

  const updateSystemPromptMutation = useMutation(
    async (promptData) => {
      const response = await axios.put(`/clients/${id}`, {
        ...client,
        chatbotConfig: {
          ...client.chatbotConfig,
          useDefaultPrompt: promptData.useDefaultPrompt,
          systemPrompt: promptData.systemPrompt,
          useDefaultNoContextMessage: promptData.useDefaultNoContextMessage,
          noContextMessage: promptData.noContextMessage
        }
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client', id]);
        toast.success('System prompt updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update system prompt');
      }
    }
  );

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const copyIntegrationCode = () => {
    const code = `<script src="${window.location.origin}/widget/chatbot.js?apiKey=${client.apiKey}"></script>`;
    copyToClipboard(code);
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="h-48 bg-gray-300 rounded"></div>
            <div className="h-32 bg-gray-300 rounded"></div>
          </div>
          <div className="space-y-6">
            <div className="h-32 bg-gray-300 rounded"></div>
            <div className="h-48 bg-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!client) {
    return <div>Client not found</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/clients"
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Clients
        </Link>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{client.name}</h1>
            <p className="text-gray-600">{client.email}</p>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              client.isActive 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {client.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client Information */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Client Information</h2>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Website</dt>
                <dd className="mt-1 text-sm text-gray-900 flex items-center">
                  <Globe className="h-4 w-4 mr-2 text-gray-400" />
                  <a 
                    href={client.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700"
                  >
                    {client.website}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(client.createdAt).toLocaleDateString()}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Total Queries</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {client.usage?.totalQueries || 0}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Monthly Queries</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {client.usage?.monthlyQueries || 0}
                </dd>
              </div>
            </dl>
          </div>

          {/* Integration Code */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Integration Code</h2>
            <p className="text-sm text-gray-600 mb-4">
              Add this script tag to your website's header to embed the chatbot:
            </p>
            <div className="bg-gray-50 p-4 rounded-md">
              <code className="text-sm text-gray-800 break-all">
                {`<script src="${window.location.origin}/widget/chatbot.js?apiKey=${client.apiKey}"></script>`}
              </code>
            </div>
            <button
              onClick={copyIntegrationCode}
              className="mt-3 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy Code
            </button>
          </div>

          {/* New Contact Messages */}
          <NewContactMessages clientId={id} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* API Key */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">API Key</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    value={client.apiKey}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50"
                  />
                </div>
                <button
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="ml-2 text-gray-500 hover:text-gray-700"
                >
                  {showApiKey ? 'Hide' : 'Show'}
                </button>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => copyToClipboard(client.apiKey)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copy
                </button>
                <button
                  onClick={() => regenerateKeyMutation.mutate()}
                  disabled={regenerateKeyMutation.isLoading}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${regenerateKeyMutation.isLoading ? 'animate-spin' : ''}`} />
                  Regenerate
                </button>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Link
                to={`/clients/${id}/context`}
                className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-50"
              >
                <MessageCircle className="h-4 w-4 mr-3 text-gray-400" />
                Manage Context
              </Link>
              <Link
                to={`/chat-sessions?clientId=${id}`}
                className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-50"
              >
                <MessageCircle className="h-4 w-4 mr-3 text-gray-400" />
                View Chat Sessions
              </Link>
              <Link
                to={`/clients/${id}/contact-submissions`}
                className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-50"
              >
                <Mail className="h-4 w-4 mr-3 text-gray-400" />
                Contact Submissions
              </Link>
              <Link
                to={`/clients/${id}/quick-questions`}
                className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-50"
              >
                <MessageSquare className="h-4 w-4 mr-3 text-gray-400" />
                Quick Questions
              </Link>
              <button 
                onClick={() => setShowSystemPromptModal(true)}
                className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-50"
              >
                <Brain className="h-4 w-4 mr-3 text-gray-400" />
                System Prompt
              </button>
              <button 
                onClick={() => setShowSitemapModal(true)}
                className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-50"
              >
                <Globe className="h-4 w-4 mr-3 text-gray-400" />
                AI Sitemap Import
              </button>
              <button className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-50">
                <BarChart3 className="h-4 w-4 mr-3 text-gray-400" />
                View Analytics
              </button>
              <button className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-50">
                <Settings className="h-4 w-4 mr-3 text-gray-400" />
                Edit Settings
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Statistics</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Context Items</span>
                <span className="text-sm font-medium text-gray-900">
                  {client.stats?.contextCount || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Recent Queries (24h)</span>
                <span className="text-sm font-medium text-gray-900">
                  {client.stats?.recentQueries || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Last Query</span>
                <span className="text-sm font-medium text-gray-900">
                  {client.usage?.lastQuery 
                    ? new Date(client.usage.lastQuery).toLocaleDateString()
                    : 'Never'
                  }
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System Prompt Modal */}
      <SystemPromptModal
        client={client}
        isOpen={showSystemPromptModal}
        onClose={() => setShowSystemPromptModal(false)}
        onSave={(promptData) => updateSystemPromptMutation.mutate(promptData)}
      />

      {/* Sitemap Config Modal */}
      <SitemapConfigModal
        client={client}
        isOpen={showSitemapModal}
        onClose={() => setShowSitemapModal(false)}
        onSave={() => {
          queryClient.invalidateQueries(['client', id]);
          setShowSitemapModal(false);
        }}
      />
    </div>
  );
};

export default ClientDetail;